import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Key } from 'ts-key-enum';

import { Close } from '@bb-ui/icons/dist/small/Close';
import { Feedback } from '@bb-ui/icons/dist/medium/Feedback';

import { BbThemeProvider } from '@bb-ui/react-library/dist/components/BbThemeProvider';
import { ClickAwayListener } from '@bb-ui/react-library/dist/components/ClickAwayListener';
import { Tooltip } from '@bb-ui/react-library/dist/components/Tooltip';
import {
    useMediaQuery,
    useTheme,
} from '@bb-ui/react-library/dist/components/styles';

import { useAuth0Context } from '../../auth/auth0/Auth0Context';
import { useAppConfigContext } from '../../contexts/AppConfigProvider';
import { useUser } from '../../hooks/useUser';
import { useFeatureToggle } from '../../featureFlags/useFeatureToggle';
import { xconsole } from '../../utilities/utilities';
import { FeedbackPanel } from './FeedbackPanel';
import { useStyles } from './FeedbackWidget.styles';
import { useSubmitFeedback } from './useSubmitFeedback';
import { hasUsedFeature, useWidgetHistory } from './useWidgetHistory';
import { EMPTY_DRAFT, FeatureId, IFeedbackDraft } from './FeedbackWidget.types';

export interface IFeedbackWidgetProps {
    /** Which feature the feedback is about; see `FeatureId` for adding one. */
    featureId: FeatureId;
    /**
     * Translation key of the feature's name, read as "the new … experience".
     * Without it the widget asks about "this new feature".
     */
    featureNameKey?: string;
    /** Set by ActionAreaButtons to match the help button; not for pages. */
    launcherClassName?: string;
    /** Spacing within the ActionAreaButtons stack, set by it. */
    className?: string;
}

interface IFeedbackWidgetContentProps extends IFeedbackWidgetProps {
    feedbackApi: string;
    tenantId: string;
    historyKey: string;
}

const TITLE_ID = 'feedback-widget-title';
const PANEL_ID = 'feedback-widget-panel';

const sha256Hex = async (value: string): Promise<string> => {
    const digest = await crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(value),
    );
    return Array.from(new Uint8Array(digest), byte =>
        byte.toString(16).padStart(2, '0'),
    ).join('');
};

/**
 * Hashed because a UAS user id spells out tenant and username, and the entry
 * outlives sign-out.
 */
export const historyStorageKey = async (
    userId: string,
    featureId: FeatureId,
): Promise<string> =>
    `bbdata.feedbackWidget.${await sha256Hex(userId)}.${featureId}`;

/** The storage key once hashed, or undefined while hashing or without a user. */
const useHistoryStorageKey = (
    userId: string | undefined,
    featureId: FeatureId,
): string | undefined => {
    const [key, setKey] = React.useState<string>();

    React.useEffect(() => {
        setKey(undefined);
        if (!userId) return undefined;

        let current = true;
        historyStorageKey(userId, featureId).then(
            hashed => {
                if (current) setKey(hashed);
            },
            // Only outside a secure context (plain http), which no deployment is.
            error =>
                xconsole.warn(
                    'Feedback widget hidden: could not hash the user id',
                    error,
                ),
        );
        return () => {
            current = false;
        };
    }, [userId, featureId]);

    return key;
};

/**
 * Launcher, panel and nudge for rating one feature. Rendered per feature area,
 * so feedback is always about the page the user is on.
 */
const FeedbackWidgetContent: React.FunctionComponent<
    IFeedbackWidgetContentProps
> = ({
    featureId,
    featureNameKey,
    launcherClassName,
    className,
    feedbackApi,
    tenantId,
    historyKey,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const theme = useTheme();
    // The launcher's tooltip does not fit left of the stack on small screens.
    const smDown = useMediaQuery(theme.breakpoints.down('sm'));

    const [open, setOpen] = React.useState(false);
    const { history, dismissPrompt } = useWidgetHistory(historyKey);

    // The nudge waits until the feature has been used, hides while the panel is
    // open, and is gone for good once dismissed or feedback is sent.
    const showPrompt =
        !open && !history.promptDismissed && hasUsedFeature(history);

    // Controlled: an emptied title still fades out as a blank pill.
    const [launcherTooltipOpen, setLauncherTooltipOpen] = React.useState(false);

    const launcherRef = React.useRef<HTMLButtonElement>(null);
    const panelRef = React.useRef<HTMLDivElement>(null);

    const { submit, status, errorKind, reset } = useSubmitFeedback(
        feedbackApi,
        tenantId,
        featureId,
    );

    // Here, not in the form, which unmounts when the panel closes.
    const [draft, setDraft] = React.useState<IFeedbackDraft>(EMPTY_DRAFT);

    const close = React.useCallback(() => {
        setOpen(false);
        launcherRef.current?.focus();
    }, []);

    // The panel, not its first control (Close), so the title is read first.
    React.useEffect(() => {
        if (open) panelRef.current?.focus();
    }, [open]);

    // On the widget, not the document, so Escape closes it only from inside.
    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (open && event.key === Key.Escape) {
            event.stopPropagation();
            close();
        }
    };

    const handleLauncherClick = () => {
        if (open) {
            close();
            return;
        }
        // Reopening starts a fresh form, except mid-send, which would lose it.
        if (status !== 'idle' && status !== 'submitting') reset();
        setOpen(true);
    };

    const featureName = featureNameKey ? t(featureNameKey) : undefined;
    // Copy naming the feature, or a generic wording when no name was given.
    const copy = (key: 'widget.prompt' | 'widget.open' | 'form.title') =>
        featureName
            ? t(`feedback.${key}`, { feature: featureName })
            : t(`feedback.${key}Generic`);
    const submitted = status === 'success';

    React.useEffect(() => {
        if (!submitted) return;
        panelRef.current?.focus();
        // Sent: clear the form and retire the nudge.
        setDraft(EMPTY_DRAFT);
        dismissPrompt();
    }, [submitted, dismissPrompt]);

    return (
        // A group rather than an <aside>: it already sits inside the help
        // area's own complementary landmark.
        <div
            role="group"
            className={`${classes.widget} ${className ?? ''}`}
            aria-label={t('feedback.widget.ariaLabel')}
        >
            <ClickAwayListener onClickAway={() => open && setOpen(false)}>
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div className={classes.anchor} onKeyDown={handleKeyDown}>
                    {open && (
                        // Without it the bb-ui inputs keep light chrome.
                        <BbThemeProvider theme="dark">
                            <FeedbackPanel
                                ref={panelRef}
                                panelId={PANEL_ID}
                                titleId={TITLE_ID}
                                title={copy('form.title')}
                                status={status}
                                errorKind={errorKind}
                                draft={draft}
                                onDraftChange={setDraft}
                                onSubmit={submit}
                                onClose={close}
                            />
                        </BbThemeProvider>
                    )}

                    {showPrompt && (
                        <div className={classes.promptWrap}>
                            {/* Separate from the launcher: the nudge needs its
                                own dismiss, and buttons cannot nest. */}
                            <button
                                type="button"
                                className={classes.prompt}
                                onClick={handleLauncherClick}
                                data-testid="feedback-prompt"
                            >
                                {copy('widget.prompt')}
                            </button>
                            <button
                                type="button"
                                className={classes.promptDismiss}
                                aria-label={t('feedback.widget.dismissPrompt')}
                                onClick={() => {
                                    dismissPrompt();
                                    // This button is gone; keep focus nearby.
                                    launcherRef.current?.focus();
                                }}
                                data-testid="feedback-prompt-dismiss"
                            >
                                <Close />
                            </button>
                        </div>
                    )}
                    {/* Hidden while the nudge or panel shows. On a wrapper so
                        the name is not read out twice. */}
                    <Tooltip
                        title={copy('widget.open')}
                        placement={smDown ? 'top-end' : 'left'}
                        open={launcherTooltipOpen && !open && !showPrompt}
                        onOpen={() => setLauncherTooltipOpen(true)}
                        onClose={() => setLauncherTooltipOpen(false)}
                    >
                        <span className={classes.tooltipAnchor}>
                            <button
                                type="button"
                                ref={launcherRef}
                                className={`${launcherClassName ?? ''} ${
                                    classes.launcherButton
                                }`}
                                onClick={handleLauncherClick}
                                aria-expanded={open}
                                aria-controls={open ? PANEL_ID : undefined}
                                aria-label={copy('widget.open')}
                                data-testid="feedback-launcher"
                            >
                                <Feedback className={classes.launcherIcon} />
                            </button>
                        </span>
                    </Tooltip>
                </div>
            </ClickAwayListener>
        </div>
    );
};

/**
 * Asks users to rate a newly released feature, from a button under the help
 * button in ActionAreaButtons.
 *
 * To collect feedback on a feature:
 * 1. Add its id to `FeatureId`, and to the backend's allowlist, which rejects
 *    any other id.
 * 2. Pass `feedback={{ featureId, featureNameKey }}` to the page's
 *    ActionAreaButtons rather than rendering this directly.
 *
 * Renders nothing unless the `userFeedbackEnabled` flag is on, the user is a
 * signed-in universal-auth user (incremental-auth tenants have no UUID for the
 * API) and the feedback API is configured. At most one per page: its element
 * ids are fixed.
 */
export const FeedbackWidget: React.FunctionComponent<
    IFeedbackWidgetProps
> = props => {
    const enabled = useFeatureToggle('userFeedbackEnabled');
    const { isAuthenticated } = useAuth0Context();
    const { api } = useAppConfigContext();
    const user = useUser();

    const feedbackApi =
        enabled && isAuthenticated ? api?.userFeedback : undefined;
    const universalUser = user?.isUniversalUser() ? user : undefined;

    // Hashing is async; the widget appears once the key is ready.
    const historyKey = useHistoryStorageKey(
        feedbackApi ? universalUser?.userId : undefined,
        props.featureId,
    );

    // A wrapper, so the body's hooks never run for someone who cannot submit.
    if (!feedbackApi || !universalUser || !historyKey) {
        return null;
    }

    return (
        <FeedbackWidgetContent
            {...props}
            feedbackApi={feedbackApi}
            tenantId={universalUser.tenantId}
            historyKey={historyKey}
        />
    );
};
